<script lang="ts">
import { CSSProperties, defineComponent, PropType, reactive, ref, toRefs, watch } from 'vue';
import CGrid from '@/components/shared/CGrid.vue';
import { Filters, FilterVal } from '@/models/Filters.model';
import {
	formatFiltersToState,
	getDataTypeLabel,
	getFilterVal,
	getSelectedBasePeriodLabel,
	getSelectedGWLLabel,
	getSelectedGridSpacingLabel,
	getSelectedScenarioLabel,
	getSelectedSeasonLabel,
	getSelectedVariableLabel,
} from '@/utils/map.utils';
import { capitalizeWord } from '@/utils/utils';
import CButton from '@/components/shared/CButton.vue';
import { ButtonType } from '@/enums/ButtonType.enum';
import CButtonRadio from '@/components/shared/CButtonRadio.vue';
import { FilterName } from '@/enums/FilterName.enum';
import { MapFiltersState } from '@/models/MapFiltersState.model';
import { FILTER_INIT_STATE } from '@/utils/constants.utils';
import CDrawer from '@/components/shared/CDrawer.vue';
import CIconLeftRight from '@/components/shared/CIconLeftRight.vue';
import CInfoRow from '@/components/shared/CInfoRow.vue';
import { FeatureSelect } from '@/models/FeatureSelect';
import { TourSelector } from '@/enums/TourSelector.enum';
import { LonLat } from '@/models/LonLat.model';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { TourStepId } from '@/enums/TourStepId.enum';

import { AuthorsResData } from '@/api/endpoints/map/map.types';

enum ComponentEvent {
	SELECT = 'select',
	SEARCH = 'search',
	SELECT_SEARCH = 'selectSearch',
	UNSELECT_SEARCH = 'unselectSearch',
	SEARCH_POINT = 'searchPoint',
	START_TOUR = 'startTour',
	SHOW_TOUR_STEP = 'showTourStep',
	CHANGE_STATE = 'changeState',
}

const filtersPriority = [
	FilterName.AUTHOR,
	FilterName.DATA,
	FilterName.VARIABLE,
	FilterName.PERIOD,
	FilterName.GWL,
	FilterName.SCENARIO,
	FilterName.SEASON,
	FilterName.GRID_SPACING,
];

const isVisibleDictInit = (() => {
	const obj = {} as Record<string, boolean>;
	Object.values(FilterName).forEach((name) => {
		obj[name] = false;
	});
	return obj;
})();

/**
 *
 * @version 1.0.0
 * @since
 */
export default defineComponent({
	name: 'MapFilters',
	components: { CInfoRow, CIconLeftRight, CDrawer, CButtonRadio, CButton, CGrid },
	props: {
		filters: {
			required: true,
			type: Object as PropType<Filters>,
		},
		lat: {
			required: false,
			type: Number,
			default: (): number | null => null,
		},
		lon: {
			required: false,
			type: Number,
			default: (): number | null => null,
		},
		searchResults: {
			required: false,
			type: Array,
			default: (): any[] => [],
		},
		toShowResults: {
			required: false,
			type: Boolean,
			default: (): boolean => true,
		},
		authors: {
			required: false,
			type: Array as PropType<AuthorsResData[]>,
			default: (): AuthorsResData[] => [],
		},
		isLoadingAuthors: {
			required: false,
			type: Boolean,
			default: (): boolean => false,
		},
		currentAuthor: {
			required: false,
			type: String,
			default: (): string => '',
		},
	},
	emits: [
		ComponentEvent.SELECT,
		ComponentEvent.SEARCH,
		ComponentEvent.SELECT_SEARCH,
		ComponentEvent.UNSELECT_SEARCH,
		ComponentEvent.SEARCH_POINT,
		ComponentEvent.START_TOUR,
		ComponentEvent.SHOW_TOUR_STEP,
		ComponentEvent.CHANGE_STATE,
	],
	setup(props, context) {
		let filters = reactive<Filters>(props.filters as Filters);
		let authors = props.authors;
		// Is used for showing data in template
		let state = reactive<MapFiltersState>(FILTER_INIT_STATE);
		const showDrawer = ref<boolean>(false);
		const isVisibleDict = ref<Record<string, boolean>>({ ...isVisibleDictInit });

		const lonCopy = ref<number>(props.lon as number);
		const latCopy = ref<number>(props.lat as number);

		state = formatFiltersToState({ filters, state, authorList: authors });

		/**
		 * Set all on false & toggles only one filter, because only one filter can be visible in drawer.
		 */
		const toggleVisibilityFilter = (filterName: FilterName): void => {
			const newValue = !isVisibleDict.value[filterName];
			isVisibleDict.value = { ...isVisibleDictInit };
			isVisibleDict.value[filterName] = newValue;

			showDrawer.value = newValue;
		};

		const setVisibilityFilter = (filterName: FilterName, value: boolean): void => {
			isVisibleDict.value = { ...isVisibleDictInit };
			isVisibleDict.value[filterName] = value;

			showDrawer.value = value;
		};

		const closeFilterDrawer = (): void => {
			isVisibleDict.value = { ...isVisibleDictInit };
			showDrawer.value = false;
		};

		watch([() => props.filters, () => props.authors], ([val, authorsList]) => {
			filters = reactive<Filters>(val as Filters);
			state = reactive<MapFiltersState>(
				formatFiltersToState({ filters, state, authorList: authorsList })
			);
			context.emit(ComponentEvent.CHANGE_STATE, state);
		});
		watch(
			() => props.lon,
			(val) => {
				lonCopy.value = val as number;
			}
		);

		watch(
			() => props.lat,
			(val) => {
				latCopy.value = val as number;
			}
		);

		const gridStyle: CSSProperties = { flexDirection: 'column', alignItems: 'flex-start' };

		return {
			TourSelector,
			TourStepId,
			faInfoCircle,
			capitalizeWord,
			FilterName,
			ComponentEvent,
			sendObj: {} as Record<string, unknown>,
			gridMargin: '6px',
			gridStyle,
			ButtonType,
			lonCopy,
			latCopy,
			...toRefs(state),
			showDrawer,
			isVisibleDict,
			toggleVisibilityFilter,
			setVisibilityFilter,
			closeFilterDrawer,
			getDataTypeLabel,
			getSelectedVariableLabel,
			getSelectedBasePeriodLabel,
			getSelectedGWLLabel,
			getSelectedScenarioLabel,
			getSelectedSeasonLabel,
			getSelectedGridSpacingLabel,
		};
	},
	data() {
		return {
			latError: null as string | null,
			lonError: null as string | null,
		};
	},
	computed: {
		filtersSelected(): Record<string, any> {
			const obj: Record<string, any> = {};
			Object.entries(this.filters || {}).forEach(([key, value]) => {
				obj[key] = value?.selected;
			});
			return obj;
		},
		activeFilterTitle() {
			const visibleFilter = Object.entries(this.isVisibleDict).find(
				([_, isVisible]) => isVisible
			)?.[0];

			const keyMap = {
				[FilterName.AUTHOR]: 'filters.author.title',
				[FilterName.VARIABLE]: 'filters.variable.title',
				[FilterName.PERIOD]: 'filters.period.title',
				[FilterName.GWL]: 'filters.gwl.title',
				[FilterName.SCENARIO]: 'filters.scenario.title',
				[FilterName.SEASON]: 'filters.season.title',
				[FilterName.GRID_SPACING]: 'filters.area.title',
			};

			return visibleFilter ? this.$t(keyMap[visibleFilter as keyof typeof keyMap]) : '';
		},
	},
	methods: {
		/**
		 * When filter is selected, only that filter & filters with higher priority will be send
		 *
		 * @param name
		 * @param value
		 */
		selectFilter(name: string, value: FilterVal): void {
			this.sendObj = {};
			let val: FilterVal;
			let filterVal: unknown;

			// Always include the currently selected author and dataType
			if (this.authorSelected) {
				this.sendObj[FilterName.AUTHOR] = this.authorSelected;
			}

			// Getting first one in filtersPriority
			let [currentFilter] = filtersPriority;
			// Going down from fist to selected filter & adding values to send object
			for (let i = 0; currentFilter !== name; i += 1, currentFilter = filtersPriority[i]) {
				val = this.filtersSelected[currentFilter];
				filterVal = getFilterVal(val);
				if (filterVal) {
					this.sendObj[currentFilter] = filterVal;
				}
			}
			// Adding selected filter
			this.sendObj[name] = getFilterVal(value);

			this.$emit(ComponentEvent.SELECT, this.sendObj);
      this.closeFilterDrawer();
		},

		selectRegion(event: FeatureSelect): void {
			if (!event?.feature) {
				return;
			}
			this.$emit(ComponentEvent.SELECT_SEARCH, event);
		},
		searchPoint({ lat, lon }: { lat: string | number; lon: string | number }): void {
			this.latError = !lat ? this.$t('message.requiredOption') : '';
			this.lonError = !lon ? this.$t('message.requiredOption') : '';

			if (!lat || !lon) {
				return;
			}

			this.$emit(ComponentEvent.SEARCH_POINT, new LonLat({ lat, lon }));
		},
		selectAuthor(author: { slug: string; name: string }): void {
			this.sendObj = {};
			this.sendObj[FilterName.AUTHOR] = author.slug;

			this.$emit(ComponentEvent.SELECT, this.sendObj);
      this.closeFilterDrawer();
		},
	},
});
</script>

<template>
	<div class="map-filter__options">
		<div class="flex-column">
			<c-info-row
				:data-tour="TourSelector.PERIOD"
				:title="$t('filters.author.title')"
				:title-info="authorSelectedLabel"
				:active="isVisibleDict[FilterName.AUTHOR]"
				:click-action="() => toggleVisibilityFilter(FilterName.AUTHOR)"
			>
				<!-- <template #iconTitle>
					<font-awesome-icon
						:icon="faInfoCircle"
						@click="
							setVisibilityFilter(FilterName.AUTHOR, true);
						  $emit(ComponentEvent.SHOW_TOUR_STEP, TourStepId.PERIOD);
						"
					/>
				</template> -->
				<template #icon>
					<c-icon-left-right :is-left="isVisibleDict[FilterName.AUTHOR]" />
				</template>
			</c-info-row>

			<div v-if="dataTypeList?.length" class="p-24" :data-tour="TourSelector.OBSERVATION">
				<div class="map-filter__data-vis-title mb-8">
					<h3 class="m-0">
						{{ $t('filters.dataType.title') }}
					</h3>
					<!-- <font-awesome-icon
						class="cursor-pointer ml-6"
						:icon="faInfoCircle"
						@click="$emit(ComponentEvent.SHOW_TOUR_STEP, TourStepId.OBSERVATION)"
					/> -->
				</div>
				<p class="map-filter__data-vis-info mb-16">
					{{ $t('filters.dataType.info') }}
				</p>
				<div class="map-filter__data-vis-btns">
					<c-button
						v-for="item in dataTypeList"
						:key="item"
						class="map-filter__data-btn"
						:name="FilterName.DATA"
						:type="ButtonType.FILTER"
						:is-active="dataTypeSelected === item"
						@click="selectFilter(FilterName.DATA, item)"
					>
						{{ getDataTypeLabel(item) }}
					</c-button>
				</div>
			</div>

			<c-info-row
				v-if="variables?.length"
				:data-tour="TourSelector.VARIABLES"
				:title="$t('filters.variable.title')"
				:title-info="getSelectedVariableLabel(variablesSelected)"
				:active="isVisibleDict[FilterName.VARIABLE]"
				:has-divider-start="true"
				:click-action="() => toggleVisibilityFilter(FilterName.VARIABLE)"
			>
				<!-- <template #iconTitle>
					<font-awesome-icon
						:class="{ 'cursor-pointer': !variablesIsDisabled }"
						:icon="faInfoCircle"
						@click="
							setVisibilityFilter(FilterName.VARIABLE, true);
							$emit(ComponentEvent.SHOW_TOUR_STEP, TourStepId.VARIABLES);
						"
					/>
				</template> -->

				<template #icon>
					<c-icon-left-right
						:is-left="isVisibleDict[FilterName.VARIABLE]"
						:disabled="variablesIsDisabled"
					/>
				</template>
			</c-info-row>

			<c-info-row
				v-if="basePeriodList?.length"
				:data-tour="TourSelector.PERIOD"
				:title="$t('filters.period.title')"
				:title-info="getSelectedBasePeriodLabel(basePeriodSelected)"
				:active="isVisibleDict[FilterName.PERIOD]"
				:click-action="() => toggleVisibilityFilter(FilterName.PERIOD)"
			>
				<!-- <template #iconTitle>
					<font-awesome-icon
						:class="{ 'cursor-pointer': !periodIsDisabled }"
						:icon="faInfoCircle"
						@click="
							setVisibilityFilter(FilterName.PERIOD, true);
							$emit(ComponentEvent.SHOW_TOUR_STEP, TourStepId.PERIOD);
						"
					/>
				</template> -->
				<template #icon>
					<c-icon-left-right
						:is-left="isVisibleDict[FilterName.PERIOD]"
						:disabled="periodIsDisabled"
					/>
				</template>
			</c-info-row>

			<c-info-row
				v-if="gwlList?.length"
				:data-tour="TourSelector.PERIOD"
				:title="$t('filters.gwl.title')"
				:title-info="getSelectedGWLLabel(gwlSelected)"
				:active="isVisibleDict[FilterName.GWL]"
				:click-action="() => toggleVisibilityFilter(FilterName.GWL)"
			>
				<!-- <template #iconTitle>
					<font-awesome-icon
						:class="{ 'cursor-pointer': !gwlList?.length }"
						:icon="faInfoCircle"
						@click="
							setVisibilityFilter(FilterName.GWL, true);
							$emit(ComponentEvent.SHOW_TOUR_STEP, TourStepId.PERIOD);
						"
					/>
				</template> -->
				<template #icon>
					<c-icon-left-right
						:is-left="isVisibleDict[FilterName.GWL]"
						:disabled="!gwlList?.length"
					/>
				</template>
			</c-info-row>

			<c-info-row
				v-if="scenarioList?.length"
				:data-tour="TourSelector.SEASON"
				:title="$t('filters.scenario.title')"
				:title-info="getSelectedScenarioLabel(scenarioSelected)"
				:active="isVisibleDict[FilterName.SCENARIO]"
				:click-action="() => toggleVisibilityFilter(FilterName.SCENARIO)"
			>
				<!-- <template #iconTitle>
					<font-awesome-icon
						:class="{ 'cursor-pointer': !seasonIsDisabled }"
						:icon="faInfoCircle"
						@click="
							setVisibilityFilter(FilterName.SCENARIO, true);
							$emit(ComponentEvent.SHOW_TOUR_STEP, TourStepId.SEASON);
						"
					/>
				</template> -->
				<template #icon>
					<c-icon-left-right
						:is-left="isVisibleDict[FilterName.SCENARIO]"
						:disabled="seasonIsDisabled"
					/>
				</template>
			</c-info-row>

			<c-info-row
				v-if="seasonList?.length"
				:data-tour="TourSelector.SEASON"
				:title="$t('filters.season.title')"
				:title-info="getSelectedSeasonLabel(seasonSelected)"
				:active="isVisibleDict[FilterName.SEASON]"
				:click-action="() => toggleVisibilityFilter(FilterName.SEASON)"
			>
				<!-- <template #iconTitle>
					<font-awesome-icon
						:class="{ 'cursor-pointer': !seasonIsDisabled }"
						:icon="faInfoCircle"
						@click="
							setVisibilityFilter(FilterName.SEASON, true);
							$emit(ComponentEvent.SHOW_TOUR_STEP, TourStepId.SEASON);
						"
					/>
				</template> -->
				<template #icon>
					<c-icon-left-right
						:is-left="isVisibleDict[FilterName.SEASON]"
						:disabled="seasonIsDisabled"
					/>
				</template>
			</c-info-row>
		</div>

		<!-- <div class="flex-center pb-24 width-100">
			<c-button
				:type="ButtonType.LINK"
				@click="$emit(ComponentEvent.START_TOUR)"
			>
				{{ $t('tour.startButton') }}
			</c-button>
		</div> -->
	</div>

	<c-drawer
		:show="showDrawer"
		:offset="{
			top: '0',
			left: '360px', // based on filters width
		}"
	>
		<template #body>
			<div class="map-filter__drawer-body">
				<div>
					<div class="flex-row items-center mb-24">
						<c-icon-left-right :is-left="showDrawer" @click="closeFilterDrawer" />
						<h5 class="m-0 ml-8 map-filter__drawer-title">
							{{ activeFilterTitle }}
						</h5>
					</div>

					<template v-if="isVisibleDict[FilterName.AUTHOR]">
						<div>
							<c-grid
								:list="authors.map((item) => ({ key: item.slug, name: item.name }))"
								:item-margin-bottom="gridMargin"
								:grid-style="gridStyle"
							>
								<template #item="{ item }">
									<c-button-radio
										:name="FilterName.AUTHOR"
										:value="item.key"
										:label="item.name"
										:checked="item.key === authorSelected"
										@check="selectAuthor({ slug: item.key, name: item.name })"
									/>
								</template>
							</c-grid>
						</div>
					</template>

					<template v-if="isVisibleDict[FilterName.VARIABLE]">
						<div class="mt-24">
							<c-grid
								:list="
									variables.map((item) => ({ key: item, name: getSelectedVariableLabel(item) }))
								"
								:item-margin-bottom="gridMargin"
								:grid-style="gridStyle"
							>
								<template #item="{ item }">
									<c-button-radio
										:name="FilterName.VARIABLE"
										:value="item.key"
										:label="item.name"
										:checked="item.key === variablesSelected"
										@check="selectFilter(FilterName.VARIABLE, item)"
									/>
								</template>
							</c-grid>
						</div>
					</template>

					<template v-if="isVisibleDict[FilterName.PERIOD]">
						<div class="mt-24">
							<c-grid
								:list="
									basePeriodList.map((item) => ({
										key: item,
										name: getSelectedBasePeriodLabel(item),
									}))
								"
								:grid-style="gridStyle"
								:item-margin-bottom="gridMargin"
							>
								<template #item="{ item }">
									<c-button-radio
										:name="FilterName.PERIOD"
										:value="item.key"
										:label="item.name"
										:checked="item.key === basePeriodSelected"
										@check="selectFilter(FilterName.PERIOD, item)"
									/>
								</template>
							</c-grid>
						</div>
					</template>

					<template v-if="isVisibleDict[FilterName.SCENARIO]">
						<div class="mt-24">
							<c-grid
								:list="
									scenarioList.map((item) => ({ key: item, name: getSelectedScenarioLabel(item) }))
								"
								:grid-style="gridStyle"
								:item-margin-bottom="gridMargin"
							>
								<template #item="{ item }">
									<c-button-radio
										:name="FilterName.SCENARIO"
										:value="item.key"
										:label="item.name"
										:checked="item.key === scenarioSelected"
										@check="selectFilter(FilterName.SCENARIO, item)"
									/>
								</template>
							</c-grid>
						</div>
					</template>

					<template v-if="isVisibleDict[FilterName.GWL]">
						<div class="mt-24">
							<c-grid
								:list="gwlList.map((item) => ({ key: item, name: getSelectedGWLLabel(item) }))"
								:grid-style="gridStyle"
								:item-margin-bottom="gridMargin"
							>
								<template #item="{ item }">
									<c-button-radio
										:name="FilterName.GWL"
										:value="item.key"
										:label="item.name"
										:checked="item.key === gwlSelected"
										@check="selectFilter(FilterName.GWL, item)"
									/>
								</template>
							</c-grid>
						</div>
					</template>

					<template v-if="isVisibleDict[FilterName.SEASON]">
						<div class="mt-24">
							<c-grid
								:list="
									seasonList.map((item) => ({ key: item, name: getSelectedSeasonLabel(item) }))
								"
								:grid-style="gridStyle"
								:item-margin-bottom="gridMargin"
							>
								<template #item="{ item }">
									<c-button-radio
										:name="FilterName.SEASON"
										:label="item.name"
										:value="item.key"
										:checked="item.key === seasonSelected"
										@check="selectFilter(FilterName.SEASON, item.key)"
									/>
								</template>
							</c-grid>
						</div>
					</template>
				</div>
			</div>
		</template>
	</c-drawer>
</template>

<style scoped lang="scss">
.map-filter__options {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: 100%;
}

.map-filter__data-vis-title {
	@include tools.font-size(14px, 19px);
	display: flex;
	justify-content: flex-start;
	align-items: center;
	color: #212529;
}

.map-filter__data-vis-btns {
	display: flex;
}

.map-filter__data-vis-info {
	@include tools.font-size(14px, 21px);

	color: #212529;
}

.map-filter__data-btn {
	width: 50%;
	flex-shrink: 0;

	&:first-child {
		border-top-right-radius: 0;
		border-bottom-right-radius: 0;
	}

	&:last-child {
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
	}
}

.map-filter__drawer-body {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: 100%;
	padding-top: tokens.$spacing-24;

	> * {
		padding-right: tokens.$spacing-24;
		padding-left: tokens.$spacing-24;
	}

	> *:last-child {
		padding-bottom: tokens.$spacing-24;
	}
}

.map-filter__drawer-title {
	@include tools.font-size(14.8px, 24px);
	font-weight: 500;
	color: #212529;
}

.map-filter__point-search {
	> *:not(:first-child) {
		margin-top: tokens.$spacing-12;
	}
}
</style>
